import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileSignature,
  Rocket,
  Zap,
  Sparkles,
  Workflow,
  CreditCard,
  Languages,
  ShieldCheck,
  Server,
  LifeBuoy,
  GitBranch,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

/**
 * Auto-rotating carousel of agency value-props. Each slide is one concrete
 * inclusion or process commitment. Premium-tone, no penalties, no "or walk
 * away free" claims — those read aggressive and tend to attract bargain-
 * hunters rather than premium clients.
 */

interface Slide {
  icon: React.ElementType;
  key: string;
  badge?: 'included' | 'highlight';
  accentColor?: string;
}

const slides: Slide[] = [
  { icon: FileSignature, key: 'contract',  badge: 'included',  accentColor: '#00D4FF' },
  { icon: Rocket,        key: 'prototype', badge: 'highlight', accentColor: '#00E5A0' },
  { icon: Zap,           key: 'deploy',    badge: 'highlight', accentColor: '#FFB020' },
  { icon: Sparkles,      key: 'ai',        badge: 'included',  accentColor: '#00D4FF' },
  { icon: Workflow,      key: 'crm',       badge: 'included',  accentColor: '#00E5A0' },
  { icon: CreditCard,    key: 'payments',  badge: 'included',  accentColor: '#00D4FF' },
  { icon: Languages,     key: 'multilang', badge: 'included',  accentColor: '#00E5A0' },
  { icon: ShieldCheck,   key: 'security',  badge: 'included',  accentColor: '#FFB020' },
  { icon: Server,        key: 'hosting',   badge: 'included',  accentColor: '#00D4FF' },
  { icon: LifeBuoy,      key: 'support',   badge: 'included',  accentColor: '#00E5A0' },
  { icon: GitBranch,     key: 'sources',   badge: 'highlight', accentColor: '#FFB020' },
] as const;

const AUTOPLAY_INTERVAL = 5000;
const TRANSITION_DURATION = 400;

export default function InfoBlocksSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(0);
  const [displayed, setDisplayed] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  // Touch state
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  // Autoplay timer ref
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const total = slides.length;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduceMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Intersection observer — reveal section header + start autoplay only when visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).classList.add('animate-fade-in-up');
                (el as HTMLElement).classList.remove('opacity-0');
              }, i * 100);
            });
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const goTo = useCallback(
    (index: number, dir: 'next' | 'prev') => {
      if (isAnimating) return;
      setDirection(dir);
      setIsAnimating(true);

      // After half transition, swap displayed slide
      setTimeout(() => {
        setDisplayed(index);
      }, TRANSITION_DURATION / 2);

      setTimeout(() => {
        setIsAnimating(false);
      }, TRANSITION_DURATION);

      setCurrent(index);
    },
    [isAnimating]
  );

  const goNext = useCallback(() => {
    goTo((current + 1) % total, 'next');
  }, [current, total, goTo]);

  const goPrev = useCallback(() => {
    goTo((current - 1 + total) % total, 'prev');
  }, [current, total, goTo]);

  // Autoplay
  useEffect(() => {
    if (!isVisible || isPaused || reduceMotion) return;
    timerRef.current = setInterval(goNext, AUTOPLAY_INTERVAL);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isVisible, isPaused, reduceMotion, goNext]);

  // Touch handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    setIsPaused(true);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;

    // Only trigger if horizontal swipe is dominant
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
      if (dx < 0) goNext();
      else goPrev();
    }

    touchStartX.current = null;
    touchStartY.current = null;
    setIsPaused(false);
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') goPrev();
    if (e.key === 'ArrowRight') goNext();
  };

  const slide = slides[displayed];
  const Icon = slide.icon;
  const accentColor = slide.accentColor ?? '#00D4FF';

  // Animation state: what CSS state does the card have right now?
  // idle → shown; animating first half → fading out + translating; animating second half → fading in
  const cardStyle = (): React.CSSProperties => {
    if (reduceMotion) return {};
    if (!isAnimating) {
      return {
        opacity: 1,
        transform: 'translateX(0)',
        transition: `opacity ${TRANSITION_DURATION / 2}ms cubic-bezier(0.16,1,0.3,1), transform ${TRANSITION_DURATION / 2}ms cubic-bezier(0.16,1,0.3,1)`,
      };
    }
    // During animation: slide exits in direction, new enters from opposite
    const exitX = direction === 'next' ? '-8px' : '8px';
    return {
      opacity: 0,
      transform: `translateX(${exitX})`,
      transition: `opacity ${TRANSITION_DURATION / 2}ms cubic-bezier(0.16,1,0.3,1), transform ${TRANSITION_DURATION / 2}ms cubic-bezier(0.16,1,0.3,1)`,
    };
  };

  const badgeLabel = (badge?: 'included' | 'highlight') => {
    if (!badge) return null;
    if (badge === 'included') return t('info.badge.included');
    return t('info.badge.highlight');
  };

  const badgeColor = (badge?: 'included' | 'highlight') =>
    badge === 'highlight'
      ? 'rgba(255,176,32,0.15)'
      : 'rgba(0,229,160,0.12)';
  const badgeTextColor = (badge?: 'included' | 'highlight') =>
    badge === 'highlight' ? '#FFB020' : '#00E5A0';
  const badgeBorder = (badge?: 'included' | 'highlight') =>
    badge === 'highlight'
      ? 'rgba(255,176,32,0.3)'
      : 'rgba(0,229,160,0.25)';

  return (
    <section
      ref={sectionRef}
      id="info"
      className="py-10 sm:py-14 lg:py-20 px-5 scroll-mt-16"
    >
      <div className="max-w-lg sm:max-w-2xl lg:max-w-3xl mx-auto">
        {/* Header */}
        <div className="reveal opacity-0 text-center mb-8 lg:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 glass-pill rounded-full mb-3">
            <Sparkles size={11} className="text-[#00D4FF]" />
            <p className="text-[#00D4FF] text-[10px] font-semibold uppercase tracking-wider">
              {t('info.label')}
            </p>
          </div>
          <h2 className="text-[#F0F2F5] text-2xl sm:text-3xl lg:text-4xl font-bold">
            {t('info.title')}
          </h2>
          <p className="text-[#8A93A7] text-xs sm:text-sm mt-2 max-w-[320px] sm:max-w-md mx-auto leading-relaxed">
            {t('info.subtitle')}
          </p>
        </div>

        {/* Carousel wrapper */}
        <div className="reveal opacity-0">
          <div
            role="region"
            aria-roledescription="carousel"
            aria-label={t('info.carouselLabel')}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocusCapture={() => setIsPaused(true)}
            onBlurCapture={() => setIsPaused(false)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            className="relative focus:outline-none"
          >
            {/* Live region for screen readers */}
            <div aria-live="polite" className="sr-only">
              {t(`info.slides.${slide.key}.title`)} — {current + 1} {t('info.of')} {total}
            </div>

            {/* Card */}
            <div
              className="relative glass-card rounded-2xl p-6 sm:p-8 lg:p-10 min-h-[240px] md:min-h-[280px] lg:min-h-[300px] flex flex-col justify-between overflow-hidden"
              style={{
                // Premium glass enhancement — stronger backdrop + cyan tint trace
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 60%, rgba(0,212,255,0.03) 100%)',
                boxShadow:
                  '0 8px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -1px 0 rgba(0,0,0,0.2)',
              }}
            >
              {/* Autoplay progress bar — visual cue that the carousel is
                  actually rotating. Re-keyed on every slide change so the
                  animation restarts from 0 → 100% over AUTOPLAY_INTERVAL.
                  Hidden under prefers-reduced-motion. */}
              {!reduceMotion && isVisible && (
                <div
                  key={`progress-${current}-${isPaused ? 'p' : 'r'}`}
                  className="absolute top-0 left-0 h-[3px] bg-gradient-to-r from-[#00D4FF] to-[#00E5A0] rounded-full"
                  style={{
                    width: '100%',
                    transformOrigin: 'left center',
                    animation: `infoProgress ${AUTOPLAY_INTERVAL}ms linear forwards`,
                    animationPlayState: isPaused ? 'paused' : 'running',
                    boxShadow: '0 0 12px rgba(0,212,255,0.5)',
                  }}
                />
              )}

              {/* Inner content — fades/slides on transition */}
              <div
                style={cardStyle()}
                aria-label={`${t(`info.slides.${slide.key}.title`)}: ${t(`info.slides.${slide.key}.desc`)}`}
              >
                {/* Top row: icon + badge */}
                <div className="flex items-start justify-between gap-4 mb-5 sm:mb-6">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: `linear-gradient(135deg, ${accentColor}22 0%, ${accentColor}0A 100%)`,
                      border: `1px solid ${accentColor}33`,
                      color: accentColor,
                    }}
                  >
                    <Icon size={22} />
                  </div>

                  {slide.badge && (
                    <span
                      className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full flex-shrink-0"
                      style={{
                        background: badgeColor(slide.badge),
                        color: badgeTextColor(slide.badge),
                        border: `1px solid ${badgeBorder(slide.badge)}`,
                      }}
                    >
                      {badgeLabel(slide.badge)}
                    </span>
                  )}
                </div>

                {/* Text */}
                <div>
                  <h3 className="text-[#F0F2F5] text-lg sm:text-xl lg:text-2xl font-bold mb-2 leading-snug">
                    {t(`info.slides.${slide.key}.title`)}
                  </h3>
                  <p className="text-[#8A93A7] text-sm sm:text-[15px] leading-relaxed max-w-[520px]">
                    {t(`info.slides.${slide.key}.desc`)}
                  </p>
                </div>
              </div>

              {/* Bottom: dots + arrows inside card */}
              <div className="flex items-center justify-between mt-6 sm:mt-8">
                {/* Dots */}
                <div
                  className="flex items-center gap-1.5"
                  role="tablist"
                  aria-label={t('info.dotsLabel')}
                >
                  {slides.map((s, i) => (
                    <button
                      key={s.key}
                      role="tab"
                      aria-selected={i === current}
                      aria-label={`${t(`info.slides.${s.key}.title`)} — ${i + 1}`}
                      onClick={() => goTo(i, i > current ? 'next' : 'prev')}
                      className="rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00D4FF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0E14]"
                      style={{
                        width: i === current ? '20px' : '6px',
                        height: '6px',
                        background: i === current ? '#00D4FF' : 'rgba(255,255,255,0.2)',
                        boxShadow: i === current ? '0 0 8px rgba(0,212,255,0.5)' : 'none',
                      }}
                    />
                  ))}
                </div>

                {/* Counter + arrows */}
                <div className="flex items-center gap-2">
                  <span className="text-[#8A93A7] text-[11px] tabular-nums mr-1">
                    {String(current + 1).padStart(2, '0')}{' '}
                    <span className="text-[rgba(255,255,255,0.2)]">/</span>{' '}
                    {String(total).padStart(2, '0')}
                  </span>

                  {/* Arrow prev — hidden on mobile, shown md+ */}
                  <button
                    onClick={goPrev}
                    aria-label={t('info.prevSlide')}
                    className="hidden md:flex w-8 h-8 items-center justify-center rounded-full glass-pill transition-all duration-200 hover:border-[rgba(0,212,255,0.4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00D4FF]"
                    style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    <ChevronLeft size={14} className="text-[#8A93A7]" />
                  </button>
                  <button
                    onClick={goNext}
                    aria-label={t('info.nextSlide')}
                    className="hidden md:flex w-8 h-8 items-center justify-center rounded-full glass-pill transition-all duration-200 hover:border-[rgba(0,212,255,0.4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00D4FF]"
                    style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    <ChevronRight size={14} className="text-[#8A93A7]" />
                  </button>
                </div>
              </div>
            </div>

            {/* Floating arrows — left/right outside card on lg+ */}
            <button
              onClick={goPrev}
              aria-label={t('info.prevSlide')}
              className="hidden lg:flex absolute left-[-52px] top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full glass-pill transition-all duration-200 hover:border-[rgba(0,212,255,0.4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00D4FF]"
              style={{ border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <ChevronLeft size={16} className="text-[#8A93A7]" />
            </button>
            <button
              onClick={goNext}
              aria-label={t('info.nextSlide')}
              className="hidden lg:flex absolute right-[-52px] top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full glass-pill transition-all duration-200 hover:border-[rgba(0,212,255,0.4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00D4FF]"
              style={{ border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <ChevronRight size={16} className="text-[#8A93A7]" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
